import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRefundById, updateRefundStatus, updateRefund, updateBanveri } from '../api/refundApi';
import { getStatuses } from '../api/statusApi';
import { getMethods } from '../api/methodApi';
import { getReasons } from '../api/reasonApi';
import { getRefundUpdateLogs } from '../api/logApi';
import { AuthContext } from '../context/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { getStatusClass, getStatusStyle } from '../utils/statusUtils';
import { usePermission } from '../context/PermissionContext';
import PermissionGuard from '../components/common/PermissionGuard';
import { 
  ArrowLeft, 
  Edit, 
  User, 
  Calendar, 
  Bookmark, 
  CreditCard, 
  Hash, 
  DollarSign,
  MessageSquare,
  PlusCircle,
  Eye,
  Download,
  Upload,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';

// Durum güncelleme modalı
const StatusUpdateModal = ({ refund, onClose, onUpdate, statuses }) => {
  const [newStatusId, setNewStatusId] = useState(refund.status_id.toString());
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubmit = async () => {
    if (newStatusId === refund.status_id.toString()) {
      onClose();
      return;
    }
    
    setIsLoading(true);
    try {
      await onUpdate(refund.id, newStatusId);
      toast.success('Durum başarıyla güncellendi');
      onClose();
    } catch (error) {
      toast.error(`Durum güncellenirken hata: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Durum Güncelle</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            &times;
          </button>
        </div>
        
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-1">İade ID: #{refund.id}</p>
            <p className="font-medium">{refund.refund_name}</p>
            <p className="text-sm text-gray-600">{refund.payment_method_name} - {refund.amount} TL</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durum
            </label>
            <select
              value={newStatusId}
              onChange={(e) => setNewStatusId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {statuses.map(status => (
                <option key={status.id} value={status.id.toString()}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-2"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Güncelleniyor...' : 'Güncelle'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Değişiklik geçmişi modal bileşeni
const ChangesHistoryModal = ({ logs, onClose, paymentMethods, reasonList, statusList }) => {
  const [expandedLogs, setExpandedLogs] = useState({});
  
  // Log içeriğindeki alanları işleyen yardımcı fonksiyon
  const formatFieldName = (fieldName) => {
    const fieldNames = {
      'payment_method_id': 'Ödeme Yöntemi',
      'reason_id': 'İade Sebebi',
      'status_id': 'Durum',
      'amount': 'Tutar',
      'member_name': 'Üye İsmi',
      'banveri': 'İade IBAN',
      'user_id': 'USER ID',
      'login_id': 'Login ID',
      'refund_name': 'İade Edilecek İsim',
      'ticket_id': 'Ticket ID',
      'receipt_image_path': 'Dekont',
      'deposit_receipt_path': 'Yatırım Dekontu'
    };
    
    return fieldNames[fieldName] || fieldName;
  };
  
  // Değeri formatla
  const formatValue = (fieldName, value, entities) => {
    if (value === null || value === undefined) return '-';
    
    if (fieldName === 'payment_method_id') {
      // Ödeme yöntemi adını bul
      const method = entities && entities.find(m => m.id.toString() === value.toString());
      return method ? method.name : value;
    }
    
    if (fieldName === 'reason_id') {
      // İade sebebi adını bul
      const reason = entities && entities.find(r => r.id.toString() === value.toString());
      return reason ? reason.name : value;
    }
    
    if (fieldName === 'status_id') {
      // Durum adını bul
      const status = entities && entities.find(s => s.id.toString() === value.toString());
      return status ? status.name : value;
    }
    
    if (fieldName === 'amount' && typeof value === 'number') {
      return value.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL';
    }
    
    return value.toString();
  };
  
  // Bir logu genişlet/daralt
  const toggleLogExpand = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center pb-3 border-b mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <Clock className="mr-2 text-primary-600" />
                Değişiklik Geçmişi
              </h3>
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={onClose}
              >
                <span className="sr-only">Kapat</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {logs.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Değişiklik bulunamadı</h3>
                <p className="mt-1 text-sm text-gray-500">Bu iade için kaydedilmiş bir değişiklik geçmişi yok.</p>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {logs.map(log => {
                  let changedFields = {};
                  try {
                    if (log.additional_data) {
                      changedFields = JSON.parse(log.additional_data);
                    }
                  } catch (e) {
                    console.error("Log verisi parse edilemedi:", e);
                  }
                  
                  const hasChanges = Object.keys(changedFields).length > 0;
                  const isExpanded = expandedLogs[log.id] || false;
                  
                  return (
                    <div key={log.id} className="bg-gray-50 rounded-lg shadow-sm p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="flex items-center text-sm font-medium text-gray-900">
                            <span className="bg-primary-100 p-1 rounded mr-2">
                              <Edit size={16} className="text-primary-600" />
                            </span>
                            {log.action === 'update_refund_status' ? 'Durum güncellendi' : 'İade güncellendi'}
                            <span className="ml-2 text-xs text-gray-500">
                              {format(new Date(log.created_at), 'dd MMMM yyyy HH:mm:ss', { locale: tr })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            <User size={14} className="inline-block mr-1" />
                            {log.username || 'Admin'} tarafından
                          </p>
                        </div>
                        
                        {hasChanges && (
                          <button 
                            onClick={() => toggleLogExpand(log.id)} 
                            className="text-primary-600 hover:text-primary-800 text-sm flex items-center"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp size={16} className="mr-1" />
                                Daralt
                              </>
                            ) : (
                              <>
                                <ChevronDown size={16} className="mr-1" />
                                Detaylar
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      
                      {hasChanges && isExpanded && (
                        <div className="mt-4 bg-white rounded-md border p-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Değişiklik Detayları</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full">
                              <thead>
                                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  <th className="px-4 py-2">Alan</th>
                                  <th className="px-4 py-2">Eski Değer</th>
                                  <th className="px-4 py-2">Yeni Değer</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {Object.entries(changedFields).map(([field, values]) => (
                                  <tr key={field}>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{formatFieldName(field)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">
                                      {field === 'payment_method_id' 
                                        ? formatValue(field, values.old, paymentMethods)
                                        : field === 'reason_id'
                                          ? formatValue(field, values.old, reasonList)
                                          : field === 'status_id'
                                            ? formatValue(field, values.old, statusList)
                                            : formatValue(field, values.old)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {field === 'payment_method_id' 
                                        ? formatValue(field, values.new, paymentMethods)
                                        : field === 'reason_id'
                                          ? formatValue(field, values.new, reasonList)
                                          : field === 'status_id'
                                            ? formatValue(field, values.new, statusList)
                                            : formatValue(field, values.new)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-100 text-base font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RefundDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { hasPermission } = usePermission();
  
  const [refund, setRefund] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [newReceipt, setNewReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [refundReceiptPath, setRefundReceiptPath] = useState(null);
  const [depositReceiptPath, setDepositReceiptPath] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [updateLogs, setUpdateLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [reasonList, setReasonList] = useState([]);
  
  // Dekont yükleme alanı referansı
  const dropzoneRef = useRef(null);
  
  // İade detaylarını, statüleri, ödeme yöntemlerini ve sebepleri yükle
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getRefundById(id);
        setRefund(data);
        
        // API yanıtını kontrol et
        console.log('İade Detay Data:', data);
        
        // Tüm olası dekont yolu alanlarını kontrol et
        console.log('Dekont yolu alanları:');
        console.log('receipt_path:', data.receipt_path);
        console.log('receipt_image_path:', data.receipt_image_path);
        console.log('receipt_image:', data.receipt_image);
        console.log('image_path:', data.image_path);
        console.log('receipt:', data.receipt);
        
        // Eğer varsa iade dekont yolunu ayarla - tüm olası alanları kontrol et
        if (data.receipt_path) {
          console.log('receipt_path mevcut:', data.receipt_path);
          setRefundReceiptPath(data.receipt_path);
        } else if (data.receipt_image_path) {
          console.log('receipt_image_path mevcut:', data.receipt_image_path);
          setRefundReceiptPath(data.receipt_image_path);
        } else if (data.receipt_image) {
          console.log('receipt_image mevcut:', data.receipt_image);
          setRefundReceiptPath(data.receipt_image);
        } else if (data.image_path) {
          console.log('image_path mevcut:', data.image_path);
          setRefundReceiptPath(data.image_path);
        } else if (data.receipt) {
          console.log('receipt mevcut:', data.receipt);
          setRefundReceiptPath(data.receipt);
        }
        
        // Eğer varsa yatırım dekont yolunu ayarla - tüm olası alanları kontrol et
        if (data.deposit_receipt_path) {
          console.log('deposit_receipt_path mevcut:', data.deposit_receipt_path);
          setDepositReceiptPath(data.deposit_receipt_path);
        } else if (data.deposit_image_path) {
          console.log('deposit_image_path mevcut:', data.deposit_image_path);
          setDepositReceiptPath(data.deposit_image_path);
        } else if (data.deposit_image) {
          console.log('deposit_image mevcut:', data.deposit_image);
          setDepositReceiptPath(data.deposit_image);
        } else if (data.deposit_receipt) {
          console.log('deposit_receipt mevcut:', data.deposit_receipt);
          setDepositReceiptPath(data.deposit_receipt);
        }
        
        // Parallel API calls için Promise.all kullan
        try {
          const [statusList, methodList, reasonsList] = await Promise.all([
            getStatuses(),
            getMethods(),
            getReasons()
          ]);
          
          setStatuses(statusList);
          setPaymentMethods(methodList);
          setReasonList(reasonsList);
          
          console.log('Statüler, ödeme yöntemleri ve sebepler yüklendi');
        } catch (apiError) {
          console.error('Referans verileri yüklenirken hata:', apiError);
        }

        // İşlem geçmişini de sayfa yüklendiğinde çek - yetki kontrolü yok
        try {
          const logs = await getRefundUpdateLogs(id);
          console.log('İade güncelleme logları:', logs);
          setUpdateLogs(logs);
        } catch (logError) {
          console.error('Log geçmişi yüklenirken hata:', logError);
        }
      } catch (error) {
        toast.error(`İade detayları yüklenirken hata: ${error.message}`);
        navigate('/refunds');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, navigate]);
  
  // Değişiklik geçmişini yükle
  const loadUpdateHistory = async () => {
    // Eğer loglar zaten yüklenmişse tekrar yükleme
    if (updateLogs.length > 0) return;
    
    setLoadingLogs(true);
    try {
      const logs = await getRefundUpdateLogs(id);
      console.log('İade güncelleme logları:', logs);
      setUpdateLogs(logs);
    } catch (error) {
      console.error('Log geçmişi yüklenirken hata:', error);
      toast.error('Değişiklik geçmişi yüklenirken bir hata oluştu');
    } finally {
      setLoadingLogs(false);
    }
  };
  
  // Değişiklik geçmişi modalını göster
  const handleShowHistoryModal = async () => {
    if (updateLogs.length === 0) {
      await loadUpdateHistory();
    }
    setShowHistoryModal(true);
  };
  
  // Resim URL'sini işleyen yardımcı fonksiyon
  const getFormattedImageUrl = (url) => {
    console.log('Formatlanacak URL:', url);
    
    if (!url) {
      console.log('URL boş, boş string döndürülüyor');
      return '';
    }
    
    // URL zaten tam yol içeriyorsa olduğu gibi kullan
    if (url.startsWith('http')) {
      console.log('URL zaten http ile başlıyor, olduğu gibi döndürülüyor:', url);
      return url;
    }
    
    // URL /uploads ile başlıyorsa olduğu gibi kullan (yeni format)
    if (url.startsWith('/uploads/')) {
      console.log('URL /uploads/ ile başlıyor, olduğu gibi döndürülüyor:', url);
      return url;
    }
    
    // URL uploads/ ile başlıyorsa / öneki ekle
    if (url.startsWith('uploads/')) {
      console.log('URL uploads/ ile başlıyor, / ekleniyor:', url);
      return `/${url}`;
    }
    
    // Eski format kontrolü - geçiş dönemi için (client/public içerenler)
    if (url.startsWith('/client/public/')) {
      console.log('URL /client/public/ ile başlıyor, yeniden formatlanıyor:', url);
      // /client/public/uploads/... -> /uploads/...
      return url.replace('/client/public/uploads/', '/uploads/').replace('/client/public/', '/');
    }
    
    // Aksi takdirde /uploads/ öneki ekle
    console.log('URL başka bir formatta, /uploads/ ekleniyor:', url);
    return `/uploads/${url}`;
  };
  
  // Dosya yükleme işlevinin ortak bölümü - handlePaste'ten önce tanımlanmalı
  const handleFileUpload = useCallback((file) => {
    if (!file) return;
    
    // Dosya tipini kontrol et
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Desteklenmeyen dosya formatı. Lütfen PNG, JPG, GIF veya PDF yükleyin.');
      return;
    }
    
    // Dosya boyutunu kontrol et (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'ı aşamaz.');
      return;
    }
    
    setNewReceipt(file);
    
    // Dosya önizlemesi
    if (file.type.includes('image')) {
      const reader = new FileReader();
      reader.onload = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // PDF dosyaları için basit bir önizleme
      setReceiptPreview('pdf');
    }
    
    // Kullanıcıya dosyanın yüklendiğini bildir
    toast.success(`Dekont yüklendi: ${file.name}`);
  }, []);
  
  // Ctrl+V ile panodan resim yapıştırma işlevi - handleFileUpload'dan sonra tanımlanmalı
  const handlePaste = useCallback((e) => {
    if (e.clipboardData && e.clipboardData.items) {
      const items = e.clipboardData.items;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          handleFileUpload(file);
          break;
        }
      }
    }
  }, [handleFileUpload]);
  
  useEffect(() => {
    // Ctrl+V ile resim yapıştırma
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);
  
  // Durum güncelleme
  const handleUpdateStatus = async (id, newStatusId) => {
    try {
      await updateRefundStatus(id, newStatusId);
      
      // İade bilgilerini güncelle
      const updatedRefund = { 
        ...refund,
        status_id: parseInt(newStatusId),
        status_name: statuses.find(s => s.id === parseInt(newStatusId))?.name || refund.status_name
      };
      
      setRefund(updatedRefund);
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // İade bilgilerini güncelleme (örn: IBAN/banveri güncellemesi)
  const handleUpdateBanveri = async (banveriValue) => {
    if (!banveriValue || !refund.id) return;
    
    try {
      console.log(`IBAN güncelleniyor: ID=${refund.id}, IBAN=${banveriValue}`);
      const updatedRefund = await updateBanveri(refund.id, banveriValue);
      
      console.log("IBAN güncelleme yanıtı:", updatedRefund);
      
      // Refund state'ini güncelle
      setRefund(updatedRefund);
      
      toast.success('IBAN bilgisi başarıyla güncellendi');
    } catch (error) {
      console.error("IBAN güncelleme hatası:", error);
      toast.error(`IBAN güncellenirken hata: ${error.message}`);
    }
  };
  
  // Sürükle-bırak işlemleri için olay işleyicileri
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.add('bg-gray-50');
    }
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.remove('bg-gray-50');
    }
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.remove('bg-gray-50');
    }
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleFileUpload(file);
    }
  }, [handleFileUpload]);
  
  // Dosya seçici değişikliği
  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);
  
  // Yeni dekont yükleme
  const handleUploadReceipt = async () => {
    if (!newReceipt) return;
    
    setUploading(true);
    try {
      // Güncelleme işlemi - sadece dekont yükleme
      const updatedData = {
        method: refund.payment_method_id,
        reason_id: refund.reason_id,
        status: refund.status_id,
        amount: refund.amount,
        receipt_image: newReceipt
      };
      
      const updatedRefund = await updateRefund(id, updatedData);
      console.log('Dekont yükleme yanıtı:', updatedRefund);
      
      // Güncellenen iade bilgilerini state'e yansıt
      setRefund(updatedRefund);
      
      // Dekont alanlarını kontrol et
      console.log('Güncellenmiş dekont alanları:');
      console.log('receipt_path:', updatedRefund.receipt_path);
      console.log('receipt_image_path:', updatedRefund.receipt_image_path);
      console.log('receipt_image:', updatedRefund.receipt_image);
      console.log('image_path:', updatedRefund.image_path);
      console.log('receipt:', updatedRefund.receipt);
      
      // Dekont bilgilerini güncelle - tüm olası alanları kontrol et
      if (updatedRefund.receipt_path && !refundReceiptPath) {
        console.log('receipt_path kullanılıyor:', updatedRefund.receipt_path);
        setRefundReceiptPath(updatedRefund.receipt_path);
      } else if (updatedRefund.receipt_image_path && !refundReceiptPath) {
        console.log('receipt_image_path kullanılıyor:', updatedRefund.receipt_image_path);
        setRefundReceiptPath(updatedRefund.receipt_image_path);
      } else if (updatedRefund.receipt_image && !refundReceiptPath) {
        console.log('receipt_image kullanılıyor:', updatedRefund.receipt_image);
        setRefundReceiptPath(updatedRefund.receipt_image);
      } else if (updatedRefund.image_path && !refundReceiptPath) {
        console.log('image_path kullanılıyor:', updatedRefund.image_path);
        setRefundReceiptPath(updatedRefund.image_path);
      } else if (updatedRefund.receipt && !refundReceiptPath) {
        console.log('receipt kullanılıyor:', updatedRefund.receipt);
        setRefundReceiptPath(updatedRefund.receipt);
      } else if (updatedRefund.deposit_receipt_path) {
        console.log('deposit_receipt_path kullanılıyor:', updatedRefund.deposit_receipt_path);
        setDepositReceiptPath(updatedRefund.deposit_receipt_path);
      } else if (updatedRefund.deposit_image_path) {
        console.log('deposit_image_path kullanılıyor:', updatedRefund.deposit_image_path);
        setDepositReceiptPath(updatedRefund.deposit_image_path);
      }
      
      setNewReceipt(null);
      setReceiptPreview(null);
      
      toast.success('Dekont başarıyla yüklendi');
    } catch (error) {
      toast.error(`Dekont yüklenirken hata: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };
  
  // Durum rengi
  const getStatusClassForDetail = (status, color) => {
    if (!color) {
      return getStatusClass(status);
    }
    return getStatusClass(status, color);
  };
  
  // Durum için style nesnesi
  const getStatusStyleForDetail = (color) => {
    return getStatusStyle(color);
  };
  
  // Yükleme durumu
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  // İade bulunamadı
  if (!refund) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">İade bulunamadı</h2>
        <p className="text-gray-600 mb-6">İstediğiniz iade kaydına erişilemedi veya silinmiş olabilir.</p>
        <button
          onClick={() => navigate('/refunds')}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
        >
          İade Listesine Dön
        </button>
      </div>
    );
  }

  return (
    <div className="py-6">
      {/* Üst Başlık */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/refunds')}
            className="mr-4 bg-gray-100 p-2 rounded-lg hover:bg-gray-200"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              İade Detayı <span className="text-gray-500">#{refund.id}</span>
            </h1>
            <p className="text-gray-600">{refund.payment_method_name} - {refund.amount} TL</p>
          </div>
        </div>
        
        {/* Durum ve Durum Güncelleme */}
        <div className="mt-4 sm:mt-0 flex items-center">
          <span 
            className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClassForDetail(refund.status_name, refund.color)}`}
            style={getStatusStyleForDetail(refund.color)}
          >
            {refund.status_name}
          </span>
          
          {/* Değişiklik Geçmişi Butonu */}
          <PermissionGuard requiredPermissions={['view_admin_panel']}>
            <button
              onClick={handleShowHistoryModal}
              className="ml-3 bg-blue-50 border border-blue-300 rounded-md px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Clock size={14} className="inline-block mr-1" />
              Geçmiş
            </button>
          </PermissionGuard>
          
          {/* Düzenle Butonu */}
          <PermissionGuard requiredPermissions={['create_refund']}>
            <button
              onClick={() => navigate(`/refunds/edit/${id}`)}
              className="ml-3 bg-white border border-gray-300 rounded-md px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Edit size={14} className="inline-block mr-1" />
              Düzenle
            </button>
          </PermissionGuard>
          
          {/* Durum Güncelleme Butonu */}
          <PermissionGuard requiredPermissions={['update_refund_status']}>
            <button
              onClick={() => setShowStatusModal(true)}
              className="ml-3 bg-white border border-gray-300 rounded-md px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Edit size={14} className="inline-block mr-1" />
              Durum Güncelle
            </button>
          </PermissionGuard>
        </div>
      </div>
      
      {/* Ana İçerik */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Detay Bilgileri */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">İade Bilgileri</h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Yöntem */}
                <PermissionGuard permission={["view_payment_methods"]}>
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="p-2 bg-primary-100 rounded-md">
                        <CreditCard size={20} className="text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-500">Ödeme Yöntemi</h3>
                      <p className="mt-1 text-base font-medium text-gray-900">{refund.payment_method_name}</p>
                    </div>
                  </div>
                </PermissionGuard>
                
                {/* Tutar */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-green-100 rounded-md">
                      <DollarSign size={20} className="text-green-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">Tutar</h3>
                    <p className="mt-1 text-base font-medium text-gray-900">
                      {typeof refund.amount === 'number' 
                        ? refund.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) 
                        : refund.amount
                      } TL
                    </p>
                  </div>
                </div>
                
                {/* İade Edilecek İsim */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-purple-100 rounded-md">
                      <User size={20} className="text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">İade Edilecek İsim</h3>
                    <p className="mt-1 text-base font-medium text-gray-900">{refund.refund_name}</p>
                  </div>
                </div>
                
                {/* Üye İsmi */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-blue-100 rounded-md">
                      <User size={20} className="text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">Üye İsmi</h3>
                    <p className="mt-1 text-base font-medium text-gray-900">{refund.member_name}</p>
                  </div>
                </div>
                
                {/* User ID */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-yellow-100 rounded-md">
                      <Hash size={20} className="text-yellow-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">USER ID</h3>
                    <p className="mt-1 text-base font-medium text-gray-900">{refund.user_id}</p>
                  </div>
                </div>
                
                {/* Login ID */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-orange-100 rounded-md">
                      <Hash size={20} className="text-orange-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">Login ID</h3>
                    <p className="mt-1 text-base font-medium text-gray-900">{refund.login_id}</p>
                  </div>
                </div>
                
                {/* Tarih */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-red-100 rounded-md">
                      <Calendar size={20} className="text-red-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">Tarih ve Saat</h3>
                    <p className="mt-1 text-base font-medium text-gray-900">
                      {refund.created_at && 
                        format(new Date(refund.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                    </p>
                  </div>
                </div>
                
                {/* Ticket ID */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-indigo-100 rounded-md">
                      <MessageSquare size={20} className="text-indigo-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">Ticket - Chat ID</h3>
                    <p className="mt-1 text-base font-medium text-gray-900">{refund.ticket_id}</p>
                  </div>
                </div>
                
                {/* IBAN Alanı */}
                {refund.banveri && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="p-2 bg-teal-100 rounded-md">
                        <CreditCard size={20} className="text-teal-600" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-500">İade IBAN</h3>
                      <p className="mt-1 text-base font-medium text-gray-900">{refund.banveri}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* İade Sebebi */}
              <div className="mt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-gray-100 rounded-md">
                      <Bookmark size={20} className="text-gray-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-500">İade Sebebi</h3>
                    <p className="mt-1 text-base text-gray-900">{refund.reason}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* İşlem Geçmişi */}
          <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">İşlem Geçmişi</h2>
            </div>
            
            <div className="p-6">
              <div className="flow-root">
                <ul className="-mb-8">
                  {/* İade oluşturulma */}
                  <li>
                    <div className="relative pb-8">
                      {(updateLogs.length > 0) && (
                        <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                      )}
                      <div className="relative flex items-start space-x-3">
                        <div className="relative">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center ring-8 ring-white">
                            <PlusCircle size={20} className="text-primary-600" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div>
                            <div className="text-sm">
                              <span className="font-medium text-gray-900">İade oluşturuldu</span>
                            </div>
                            <p className="mt-0.5 text-sm text-gray-500">
                              {refund.created_at && 
                                format(new Date(refund.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                            </p>
                          </div>
                          <div className="mt-2 text-sm text-gray-700">
                            <p>
                              <PermissionGuard permission={["view_payment_methods"]}>
                                {refund.payment_method_name} yöntemiyle 
                              </PermissionGuard>
                              {refund.amount} TL tutarında iade talebi oluşturuldu.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                  
                  {/* Dinamik işlem geçmişi - tüm loglar (en yeni en üstte) */}
                  {updateLogs.slice().reverse().map((log, index) => {
                    let changedFields = {};
                    try {
                      if (log.additional_data) {
                        changedFields = JSON.parse(log.additional_data);
                      }
                    } catch (e) {
                      console.error("Log verisi parse edilemedi:", e);
                    }

                    // Log içeriğindeki alanları işleyen yardımcı fonksiyon
                    const formatFieldName = (fieldName) => {
                      const fieldNames = {
                        'payment_method_id': 'Ödeme Yöntemi',
                        'reason_id': 'İade Sebebi',
                        'status_id': 'Durum',
                        'amount': 'Tutar',
                        'member_name': 'Üye İsmi',
                        'banveri': 'İade IBAN',
                        'user_id': 'USER ID',
                        'login_id': 'Login ID',
                        'refund_name': 'İade Edilecek İsim',
                        'ticket_id': 'Ticket ID',
                        'receipt_image_path': 'Dekont',
                        'deposit_receipt_path': 'Yatırım Dekontu'
                      };
                      
                      return fieldNames[fieldName] || fieldName;
                    };

                    // Değeri formatla
                    const formatValue = (fieldName, value, entities) => {
                      if (value === null || value === undefined) return '-';
                      
                      if (fieldName === 'payment_method_id') {
                        const method = entities && entities.find(m => m.id.toString() === value.toString());
                        return method ? method.name : value;
                      }
                      
                      if (fieldName === 'reason_id') {
                        const reason = entities && entities.find(r => r.id.toString() === value.toString());
                        return reason ? reason.name : value;
                      }
                      
                      if (fieldName === 'status_id') {
                        const status = entities && entities.find(s => s.id.toString() === value.toString());
                        return status ? status.name : value;
                      }
                      
                      if (fieldName === 'amount' && typeof value === 'number') {
                        return value.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL';
                      }
                      
                      return value.toString();
                    };

                    return (
                      <li key={log.id}>
                        <div className="relative pb-8">
                          {(index < updateLogs.length - 1) && (
                            <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                          )}
                          <div className="relative flex items-start space-x-3">
                            <div className="relative">
                              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center ring-8 ring-white">
                                <Edit size={20} className="text-green-600" />
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div>
                                <div className="text-sm">
                                  <span className="font-medium text-gray-900">
                                    {log.action === 'update_refund_status' ? (() => {
                                      // Log detayından yeni durum ID'sini çıkar
                                      const statusMatch = log.details && log.details.match(/Status=(\d+)/);
                                      if (statusMatch && statuses.length > 0) {
                                        const statusId = parseInt(statusMatch[1]);
                                        const status = statuses.find(s => s.id === statusId);
                                        return `Durum güncellendi: ${status ? status.name : 'Bilinmeyen durum'}`;
                                      }
                                      return 'Durum güncellendi';
                                    })() : 'İade güncellendi'}
                                  </span>
                                </div>
                                <p className="mt-0.5 text-sm text-gray-500">
                                  {format(new Date(log.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                                  {log.username && (
                                    <span className="ml-2">• {log.username} tarafından</span>
                                  )}
                                </p>
                              </div>
                              {Object.keys(changedFields).length > 0 && (
                                <div className="mt-2 text-sm text-gray-700">
                                  <div className="bg-gray-50 rounded-md p-3 mt-2">
                                    <p className="font-medium text-gray-900 mb-2">Değişiklikler:</p>
                                    {Object.entries(changedFields).map(([field, values]) => (
                                      <div key={field} className="mb-1 text-xs">
                                        <span className="font-medium">{formatFieldName(field)}:</span>
                                        <span className="ml-1 text-red-600">
                                          {field === 'payment_method_id' 
                                            ? formatValue(field, values.old, paymentMethods)
                                            : field === 'reason_id'
                                              ? formatValue(field, values.old, reasonList)
                                              : field === 'status_id'
                                                ? formatValue(field, values.old, statuses)
                                                : formatValue(field, values.old)}
                                        </span>
                                        <span className="mx-1">→</span>
                                        <span className="text-green-600">
                                          {field === 'payment_method_id' 
                                            ? formatValue(field, values.new, paymentMethods)
                                            : field === 'reason_id'
                                              ? formatValue(field, values.new, reasonList)
                                              : field === 'status_id'
                                                ? formatValue(field, values.new, statuses)
                                                : formatValue(field, values.new)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Sağ Kenar Çubuğu */}
        <div>
          {/* İade Dekontu Kartı */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">Yatırım Dekontu</h2>
            </div>
            
            <div className="p-6">
              {refundReceiptPath ? (
                <div className="flex flex-col items-center">
                  <div className="w-full mb-4 border rounded overflow-hidden">
                    <img
                      src={getFormattedImageUrl(refundReceiptPath)}
                      alt="İade Dekontu"
                      className="w-full max-h-80 object-contain"
                    />
                  </div>
                  <div className="mt-2 flex space-x-2">
                    <a
                      href={getFormattedImageUrl(refundReceiptPath)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 flex items-center"
                    >
                      <Eye size={16} className="mr-1" />
                      Görüntüle
                    </a>
                    <a
                      href={getFormattedImageUrl(refundReceiptPath)}
                      download
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 flex items-center"
                    >
                      <Download size={16} className="mr-1" />
                      İndir
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Yatırım dekontu yüklenmemiş</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Yatırım Dekontu Kartı */}
          <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">İade Dekontu</h2>
            </div>
            
            <div className="p-6">
              {depositReceiptPath ? (
                <div className="flex flex-col items-center">
                  <div className="w-full mb-4 border rounded overflow-hidden">
                    <img
                      src={getFormattedImageUrl(depositReceiptPath)}
                      alt="Yatırım Dekontu"
                      className="w-full max-h-80 object-contain"
                    />
                  </div>
                  <div className="mt-2 flex space-x-2">
                    <a
                      href={getFormattedImageUrl(depositReceiptPath)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 flex items-center"
                    >
                      <Eye size={16} className="mr-1" />
                      Görüntüle
                    </a>
                    <a
                      href={getFormattedImageUrl(depositReceiptPath)}
                      download
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 flex items-center"
                    >
                      <Download size={16} className="mr-1" />
                      İndir
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>İade dekontu yüklenmemiş</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Özet Bilgiler */}
          <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">Özet Bilgiler</h2>
            </div>
            
            <div className="p-6">
              <dl className="divide-y divide-gray-200">
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">İade ID</dt>
                  <dd className="text-sm font-medium text-gray-900">#{refund.id}</dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Oluşturan</dt>
                  <dd className="text-sm font-medium text-gray-900">{refund.created_by_username || 'Admin'}</dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Oluşturulma Tarihi</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {refund.created_at && 
                      format(new Date(refund.created_at), 'dd.MM.yyyy', { locale: tr })}
                  </dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Son Güncelleme</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {refund.updated_at && (
                      <>
                        {format(new Date(refund.updated_at), 'dd.MM.yyyy', { locale: tr })}
                        {refund.updated_by_username && (
                          <span className="ml-1">({refund.updated_by_username})</span>
                        )}
                      </>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          
          {/* Dekont Yükleme Kartı */}
          <PermissionGuard requiredPermissions={['update_refund']}>
            <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-medium text-gray-900">İade Dekontu Yükle</h2>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  {receiptPreview ? (
                    <div className="mb-4 border rounded p-2">
                      <img
                        src={receiptPreview}
                        alt="Önizleme"
                        className="w-full h-48 object-contain"
                      />
                      <button
                        onClick={() => {
                          setNewReceipt(null);
                          setReceiptPreview(null);
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Kaldır
                      </button>
                    </div>
                  ) : (
                    <div
                      ref={dropzoneRef}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50"
                      onClick={() => document.getElementById('receipt-upload').click()}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm font-medium text-gray-900">Dekont yüklemek için tıklayın</p>
                      <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF veya PDF</p>
                      <p className="mt-1 text-xs text-gray-500">Sürükleyip bırakabilir veya Ctrl+V ile yapıştırabilirsiniz</p>
                      <input 
                        id="receipt-upload"
                        type="file" 
                        className="hidden"
                        accept="image/*,.pdf"
                        onChange={handleFileChange}
                      />
                    </div>
                  )}
                </div>
                
                {newReceipt && (
                  <button
                    onClick={handleUploadReceipt}
                    disabled={uploading}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {uploading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Yükleniyor...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-1" />
                        Yükle
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </PermissionGuard>
        </div>
      </div>
      
      {/* Durum Güncelleme Modalı */}
      {showStatusModal && (
        <StatusUpdateModal
          refund={refund}
          onClose={() => setShowStatusModal(false)}
          onUpdate={handleUpdateStatus}
          statuses={statuses}
        />
      )}
      
      {/* Değişiklik Geçmişi Modalı */}
      {showHistoryModal && (
        <ChangesHistoryModal
          logs={updateLogs}
          onClose={() => setShowHistoryModal(false)}
          paymentMethods={paymentMethods}
          reasonList={reasonList}
          statusList={statuses}
        />
      )}
    </div>
  );
};

export default RefundDetailPage;